import {Field} from 'react-final-form';
import {Link} from 'react-router-dom';

import PageContainer from 'src/components/PageContainer';
import PageTitle from 'src/components/PageTitle';
import {useTranslations} from 'src/components/TranslationsContext';
import {useCurrentOrganization} from 'src/hooks/useAuth';
import {useOrganizationUpdateMutation} from 'src/hooks/useOrganization';
import {ApiUserProfile} from 'src/lib/api/auth';
import {ORG_SETTING_MCP_DISABLED} from 'src/lib/constants';
import {makeUuidPrefix} from 'src/lib/routeUtils';
import * as tw from 'src/lib/tailwindClasses';
import FormSection, {FormSectionSeparator} from 'src/pages/Settings/FormSection';

type Props = {
  currentUser: ApiUserProfile;
};

export default function Organization({currentUser}: Props) {
  const {t} = useTranslations();
  const canEdit = currentUser.role === 'owner' || currentUser.role === 'admin';
  const isOwner = currentUser.role === 'owner';
  const currentOrg = useCurrentOrganization()!;
  const updateOrganization = useOrganizationUpdateMutation(currentOrg);
  const mcpAccess = currentOrg.settings[ORG_SETTING_MCP_DISABLED] === true ? 'off' : 'enabled';

  return (
    <PageContainer>
      <PageTitle
        pageStack={[
          {title: currentOrg.name, to: '/:currentOrgIdPrefix/projects'},
          {title: t('settings'), to: `/:currentOrgIdPrefix/settings`},
          {title: t('organization'), to: `/:currentOrgIdPrefix/settings/organization`},
        ]}
      />
      <FormSection
        name={t('organization')}
        isLoading={updateOrganization.isLoading}
        initialValues={{name: currentOrg.name}}
        onSubmit={(values) => updateOrganization.mutate({name: values.name})}
        hasPermissions={canEdit}
        render={() => {
          return (
            <>
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="name-input" className="block text-sm font-medium text-gray-700">
                  {t('organizationNameLabel')}
                </label>
                <Field name="name">
                  {({input}) => (
                    <input
                      id="name-input"
                      type="text"
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      disabled={!canEdit}
                      {...input}
                    />
                  )}
                </Field>
              </div>
            </>
          );
        }}
      />
      <FormSectionSeparator />
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <div className="px-4 sm:px-0">
            <h3 className="text-lg font-medium leading-6 text-gray-900">{t('mcpAccess')}</h3>
            <div className="mt-1 text-sm text-gray-600">
              {t('mcpAccessDescription')}{' '}
              <a
                target="_blank"
                href="https://hydroforecast.com/support/article/hydroforecast-mcp/"
                rel="noreferrer"
                className={tw.link}
              >
                {t('learnMoreHere')}.
              </a>
            </div>
          </div>
        </div>
        <div className="mt-5 md:mt-0 md:col-span-2">
          <div className="shadow sm:rounded-md sm:overflow-hidden">
            <div className="px-4 py-5 bg-white space-y-3 sm:p-6">
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="mcp-access" className="block text-sm font-medium text-gray-700">
                  {t('mcpAccess')}
                </label>
                <select
                  id="mcp-access"
                  className={`${tw.formSelect} max-w-xs`}
                  disabled={!isOwner || updateOrganization.isLoading}
                  value={mcpAccess}
                  onChange={(event) =>
                    updateOrganization.mutate({
                      settings: {
                        [ORG_SETTING_MCP_DISABLED]: event.currentTarget.value === 'off',
                      },
                    })
                  }
                >
                  <option value="enabled">{t('mcpAccessEnabled')}</option>
                  <option value="off">{t('mcpAccessOff')}</option>
                </select>
              </div>
              <div className="text-sm text-gray-600">
                {isOwner ? (
                  t('mcpAccessOwnerHelp')
                ) : (
                  <>
                    {t('mcpAccessOwnerOnly')}{' '}
                    <Link
                      className={tw.link}
                      to={`/${makeUuidPrefix(currentOrg.id)}/settings/users`}
                    >
                      {t('teamPage')}
                    </Link>
                    .
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
